import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";

export default function Settings() {
  const { profile, session } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [fullName, setFullName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (profile) setFullName(profile.full_name || "");
  }, [profile]);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("agency_settings").select("*").eq("id", 1).single();
    setSettings(data);
    setLoading(false);
  }

  function update(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  async function saveSettings() {
    setSaving(true);
    setMessage(null);
    const { error } = await supabase
      .from("agency_settings")
      .update({
        agency_name: settings.agency_name,
        contact_email: settings.contact_email,
        phone: settings.phone,
        business_address: settings.business_address,
        accent_color: settings.accent_color,
        default_currency: settings.default_currency,
        default_payment_terms: settings.default_payment_terms,
        updated_at: new Date().toISOString(),
      })
      .eq("id", 1);
    setSaving(false);
    setMessage(error ? error.message : "Saved.");
  }

  async function saveOwnName() {
    await supabase.from("users").update({ full_name: fullName }).eq("id", profile.id);
    setMessage("Name updated.");
  }

  async function changePassword() {
    if (newPassword.length < 6) {
      setPasswordMessage("Password must be at least 6 characters.");
      return;
    }
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSavingPassword(false);
    if (error) {
      setPasswordMessage(error.message);
      return;
    }
    setNewPassword("");
    setPasswordMessage("Password updated.");
  }

  if (loading || !settings) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-semibold text-foreground mb-1">Settings</h1>
      <p className="text-sm text-muted-foreground mb-6">Agency profile, branding, and invoice defaults</p>

      <div className="grid grid-cols-[160px_1fr] gap-6 items-start">
        <nav className="flex flex-col gap-1 sticky top-8">
          <a href="#profile" className="text-sm px-2.5 py-2 rounded-lg hover:bg-accent">Agency profile</a>
          <a href="#branding" className="text-sm px-2.5 py-2 rounded-lg hover:bg-accent">Branding</a>
          <a href="#invoicing" className="text-sm px-2.5 py-2 rounded-lg hover:bg-accent">Invoice defaults</a>
          <a href="#account" className="text-sm px-2.5 py-2 rounded-lg hover:bg-accent">Account &amp; security</a>
        </nav>

        <div>
          <Section id="profile" title="Agency profile" sub="Shown on the client dashboard header">
            <FieldRow label="Agency name">
              <Input value={settings.agency_name || ""} onChange={(e) => update("agency_name", e.target.value)} />
            </FieldRow>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Contact email">
                <Input value={settings.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} />
              </FieldRow>
              <FieldRow label="Phone (optional)">
                <Input value={settings.phone || ""} onChange={(e) => update("phone", e.target.value)} />
              </FieldRow>
            </div>
            <FieldRow label="Business address">
              <Input value={settings.business_address || ""} onChange={(e) => update("business_address", e.target.value)} placeholder="Used on invoice paperwork" />
            </FieldRow>
          </Section>

          <Section id="branding" title="Branding" sub="Colors used on the client dashboard">
            <FieldRow label="Primary accent">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.accent_color || "#000d14"}
                  onChange={(e) => update("accent_color", e.target.value)}
                  className="w-10 h-10 rounded-lg border border-input"
                />
                <span className="font-mono text-sm text-muted-foreground">{settings.accent_color}</span>
              </div>
            </FieldRow>
            <p className="text-[11px] text-muted-foreground">
              This is stored for reference, applying it live across the dashboard's theme is a further step, right now
              the app still uses the CSS variables already defined in your site's design system.
            </p>
          </Section>

          <Section id="invoicing" title="Invoice defaults" sub="Applied to every new invoice">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Numbering format">
                <Input value="AGY-00001, continuous" disabled className="bg-accent text-muted-foreground" />
              </FieldRow>
              <FieldRow label="Default currency">
                <Input value={settings.default_currency || ""} onChange={(e) => update("default_currency", e.target.value)} />
              </FieldRow>
            </div>
            <FieldRow label="Default payment terms">
              <Input
                value={settings.default_payment_terms || ""}
                onChange={(e) => update("default_payment_terms", e.target.value)}
                placeholder="e.g. Due within 15 days of receipt"
              />
            </FieldRow>
          </Section>

          {message && <p className="text-xs text-muted-foreground mb-3">{message}</p>}
          <button
            onClick={saveSettings}
            disabled={saving}
            className="bg-primary text-primary-foreground rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-60 mb-8"
          >
            {saving ? "Saving..." : "Save settings"}
          </button>

          <Section id="account" title="Account & security" sub="Your own login">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <FieldRow label="Your name">
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </FieldRow>
              <FieldRow label="Your email">
                <Input value={session?.user?.email || ""} disabled className="bg-accent text-muted-foreground" />
              </FieldRow>
            </div>
            <button onClick={saveOwnName} className="border border-input rounded-lg px-4 py-2 text-sm mb-5">
              Save name
            </button>

            <FieldRow label="New password">
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </FieldRow>
            {passwordMessage && <p className="text-xs text-muted-foreground mb-2">{passwordMessage}</p>}
            <button
              onClick={changePassword}
              disabled={savingPassword}
              className="border border-input rounded-lg px-4 py-2 text-sm disabled:opacity-60"
            >
              {savingPassword ? "Updating..." : "Change password"}
            </button>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ id, title, sub, children }) {
  return (
    <div id={id} className="border border-border rounded-2xl bg-card p-5 mb-5 scroll-mt-8">
      <div className="text-sm font-semibold text-foreground mb-0.5">{title}</div>
      <p className="text-xs text-muted-foreground mb-4">{sub}</p>
      {children}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={`w-full border border-input rounded-lg px-3 py-2 text-sm bg-background text-foreground ${props.className || ""}`}
    />
  );
}
